export type OtpSendCooldownChannel = 'phone' | 'email' | 'invite' | 'qr-join'

export type OtpSendDeadlineKind = 'refusal' | 'send'

const KEY_PREFIX = 'bb:otp-send-cooldown:'
const SEND_KEY_PREFIX = `${KEY_PREFIX}send:`

const listeners = new Set<() => void>()

function notifyListeners(): void {
  listeners.forEach((listener) => listener())
}

export function subscribeDeadlines(listener: () => void): () => void {
  listeners.add(listener)
  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith(KEY_PREFIX)) listener()
  }
  if (typeof window !== 'undefined') window.addEventListener('storage', handleStorage)
  return () => {
    listeners.delete(listener)
    if (typeof window !== 'undefined') window.removeEventListener('storage', handleStorage)
  }
}

function normalizeIdentifier(channel: OtpSendCooldownChannel, identifier: string): string {
  if (channel === 'email' || channel === 'qr-join') return identifier.trim().toLowerCase()
  if (channel === 'invite') return identifier.trim()
  return identifier.replace(/\D/g, '')
}

export function otpSendCooldownKey(channel: OtpSendCooldownChannel, identifier: string): string {
  return `${KEY_PREFIX}${channel}:${normalizeIdentifier(channel, identifier)}`
}

function keyForKind(
  kind: OtpSendDeadlineKind,
  channel: OtpSendCooldownChannel,
  identifier: string,
): string {
  if (kind === 'refusal') return otpSendCooldownKey(channel, identifier)
  return `${SEND_KEY_PREFIX}${channel}:${normalizeIdentifier(channel, identifier)}`
}

function readKey(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === null) return null
    const deadline = Number(raw)
    if (!Number.isFinite(deadline) || deadline <= Date.now()) {
      window.localStorage.removeItem(key)
      return null
    }
    return deadline
  } catch {
    return null
  }
}

export function readDeadlineOfKind(
  kind: OtpSendDeadlineKind,
  channel: OtpSendCooldownChannel,
  identifier: string,
): number | null {
  if (typeof window === 'undefined') return null
  return readKey(keyForKind(kind, channel, identifier))
}

export function readDeadline(channel: OtpSendCooldownChannel, identifier: string): number | null {
  const refusal = readDeadlineOfKind('refusal', channel, identifier)
  const send = readDeadlineOfKind('send', channel, identifier)
  if (refusal === null) return send
  if (send === null) return refusal
  return Math.max(refusal, send)
}

export function writeDeadline(
  channel: OtpSendCooldownChannel,
  identifier: string,
  deadline: number,
  kind: OtpSendDeadlineKind = 'refusal',
): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(keyForKind(kind, channel, identifier), String(deadline))
  } catch {
    return
  } finally {
    notifyListeners()
  }
}

export function clearDeadline(
  channel: OtpSendCooldownChannel,
  identifier: string,
  kind?: OtpSendDeadlineKind,
): void {
  if (typeof window === 'undefined') return
  try {
    if (kind === undefined || kind === 'refusal') {
      window.localStorage.removeItem(keyForKind('refusal', channel, identifier))
    }
    if (kind === undefined || kind === 'send') {
      window.localStorage.removeItem(keyForKind('send', channel, identifier))
    }
  } catch {
    return
  } finally {
    notifyListeners()
  }
}
